import type { ReactNode } from 'react';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';

import type { AnchorHTMLAttributesWithRef } from 'types';
import { cn } from 'utils/classNames';

import AnchorLink from './AnchorLink';

export interface Props
	extends
		Omit<AnchorHTMLAttributesWithRef<HTMLAnchorElement>, 'href'>,
		Omit<NextLinkProps, 'href' | 'legacyBehavior'> {
	/** Element ID, is appended to `href` with #. */
	anchor?: string;

	/** Link text/content. */
	children: ReactNode;

	/** Link class names. */
	className?: string;

	/** Stretch the link to cover everything to its nearest relative ancestor. */
	cover?: boolean;

	/** If the link's hit area should be invisibly expanded outside its natural size. */
	hasExpandedHitArea?: boolean;

	/** Link URL. */
	href: string;

	/** Hide the link from screen readers. */
	isScreenReaderHidden?: boolean;

	/** Underline the text. */
	underline?: boolean;
}

/** Wrapped Next.js Link */
export default function Link({
	anchor,
	children,
	className,
	cover = false,
	hasExpandedHitArea = false,
	href: hrefProp,
	isScreenReaderHidden = false,
	ref,
	target,
	underline = false,

	// Extract Next link props to not pass them to AnchorLink
	/* oxlint-disable perfectionist/sort-objects */
	passHref,
	prefetch = false,
	replace,
	scroll,
	shallow,
	/* oxlint-enable perfectionist/sort-objects */

	...props
}: Props) {
	// Let the anchor prop override any href anchors.
	const href = anchor
		? `${hrefProp.split('#')[0]}#${anchor.replace('#', '')}`
		: hrefProp;

	if (!href || !children) {
		return null;
	}

	const commonProps: AnchorHTMLAttributesWithRef<HTMLAnchorElement> = {
		'aria-hidden': isScreenReaderHidden ? true : props['aria-hidden'],
		'tabIndex': isScreenReaderHidden ? -1 : props.tabIndex,
		'className': cn(
			className,
			underline && 'underline hover:underline-strong',
			cover && 'before:absolute before:inset-0 before:z-2',
			// Only expand vertically, most links should naturally be wide enough.
			hasExpandedHitArea &&
				'relative after:absolute after:inset-x-0 after:-inset-y-2',
		),
	};
	const nextProps = {
		replace,
		scroll,
		shallow,
		passHref,
		prefetch,
	};

	if (href.startsWith('#')) {
		// Don't include target here since we should stay on the same page.
		return (
			<AnchorLink {...props} {...commonProps} ref={ref} anchor={href}>
				{children}
			</AnchorLink>
		);
	}

	return (
		<NextLink
			{...props}
			{...commonProps}
			{...nextProps}
			ref={ref}
			href={href}
			// Target could be an empty string
			target={target || undefined}
		>
			{children}
		</NextLink>
	);
}
Link.displayName = 'Link';
